<template>
  <div class="kitchen-login">
    <div class="kitchen-login__panel">
      <div class="kitchen-login__brand">
        <img :src="logoUrl" :alt="brandName" class="kitchen-login__logo" />
        <h1 class="kitchen-login__title">Kitchen Display</h1>
        <p class="kitchen-login__subtitle">Sign in to manage live orders</p>
      </div>

      <div v-if="failed" class="kitchen-login__error" role="alert">
        Invalid username or password. Please try again.
      </div>

      <form class="kitchen-login__form" @submit.prevent="Login">
        <label class="kitchen-login__field">
          <span>Username</span>
          <input
            v-model="user.username"
            type="text"
            autocomplete="username"
            placeholder="Enter username"
            :disabled="loading"
          />
        </label>

        <label class="kitchen-login__field" v-if="!accessChoices.length">
          <span>Password</span>
          <input
            v-model="user.password"
            type="password"
            autocomplete="current-password"
            placeholder="Enter password"
            :disabled="loading"
            @keyup.enter="Login"
          />
        </label>

        <div v-else class="kitchen-login__choices">
          <p class="kitchen-login__lead">
            This account can open more than one screen. Choose one to continue. You sign in to only that screen.
          </p>
          <button
            v-for="choice in accessChoices"
            :key="choice.value"
            type="button"
            class="kitchen-login__choice"
            :disabled="loading"
            @click="chooseAccess(choice.value)"
          >
            <span>{{ choice.label }}</span>
            <small>{{ accessHint(choice.value) }}</small>
          </button>
          <button type="button" class="kitchen-login__back" @click="clearAccessChoice">
            Back to password
          </button>
        </div>

        <button
          v-if="!accessChoices.length"
          type="submit"
          class="kitchen-login__submit"
          :disabled="loading"
        >
          <span v-if="!loading">Sign In</span>
          <span v-else>Signing in…</span>
        </button>
      </form>

      <p class="kitchen-login__footer">Speed Code Labs</p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getDefaultRouteForToken, getValidToken } from "@/utils/auth";

export default {
  name: "KitchenLogin",
  data: () => ({
    loading: false,
    failed: false,
    accessChoices: [],
    user: { username: "", password: "", usertype: null },
  }),
  computed: {
    ...mapGetters("organization", ["logoUrl", "brandName"]),
    ...mapGetters("auth", ["IsAuthenticated"]),
  },
  mounted() {
    if (this.IsAuthenticated) {
      const token = getValidToken();
      const redirect = this.$route.query.redirect;
      if (redirect && redirect !== "/kitchen/login") {
        this.$router.replace(redirect);
        return;
      }
      this.$router.replace(getDefaultRouteForToken(token));
    }
    this.$store.dispatch("organization/fetchOrganization").catch(() => {});
  },
  methods: {
    chooseAccess(type) {
      this.user.usertype = type;
      this.Login();
    },
    clearAccessChoice() {
      this.accessChoices = [];
      this.user.usertype = null;
    },
    accessHint(value) {
      const hints = {
        1: "Opens the order screen. Take table orders and prepare the bill.",
        2: "Opens the kitchen display. See tickets and update cooking status.",
        3: "Opens the waiter screen. Serve your tables and follow your orders.",
      };
      return hints[Number(value)] || "";
    },
    async Login() {
      if (!this.user.username || !this.user.password) {
        this.failed = true;
        return;
      }
      this.loading = true;
      this.failed = false;
      try {
        const res = await this.$store.dispatch("auth/Login", this.user);
        const data = res && res.data ? res.data : {};
        if (data.chooseAccess) {
          this.accessChoices = data.accessTypes || [];
          return;
        }
        await this.$store.dispatch("sitesetting/GetSettings").catch(() => {});
        const token = getValidToken();
        const userType = token ? getDefaultRouteForToken(token) : "/kitchen/orders";
        const redirect = this.$route.query.redirect;
        if (redirect && redirect !== "/kitchen/login") {
          this.$router.push(redirect);
        } else if (userType === "/kitchen/orders") {
          this.$router.push("/kitchen/orders");
        } else {
          this.$router.push(userType);
        }
      } catch (err) {
        this.failed = true;
        this.accessChoices = [];
        this.user.usertype = null;
        this.user.password = "";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.kitchen-login {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: linear-gradient(145deg, #f8fafc 0%, #e2e8f0 100%);
  box-sizing: border-box;
}

.kitchen-login__panel {
  width: 100%;
  max-width: 420px;
  padding: 36px 32px 28px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.08);
  text-align: center;
}

.kitchen-login__brand {
  margin-bottom: 28px;
}

.kitchen-login__logo {
  width: 64px;
  height: 64px;
  object-fit: contain;
  margin-bottom: 16px;
}

.kitchen-login__title {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
}

.kitchen-login__subtitle {
  margin: 0;
  font-size: 14px;
  color: #64748b;
}

.kitchen-login__error {
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 13px;
  text-align: left;
}

.kitchen-login__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: left;
}

.kitchen-login__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.kitchen-login__field span {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.kitchen-login__field input {
  height: 46px;
  padding: 0 14px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 15px;
  color: #0f172a;
  background: #fff;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.kitchen-login__field input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.kitchen-login__choice,
.kitchen-login__submit {
  margin-top: 8px;
  height: 48px;
  border: none;
  border-radius: 10px;
  background: #3b82f6;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
}

.kitchen-login__choices {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.kitchen-login__lead {
  margin: 0;
  color: #475569;
  font-size: 14px;
  line-height: 1.5;
  font-weight: 400;
}

.kitchen-login__choice {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  height: auto;
  margin-top: 0;
  padding: 14px 16px;
  text-align: left;
  background: #ffffff;
  color: #0f172a;
  border: 1px solid #cbd5e1;
}

.kitchen-login__choice small {
  color: #64748b;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;
}

.kitchen-login__choice:hover:not(:disabled) {
  border-color: #3b82f6;
  background: #f8fafc;
}

.kitchen-login__back {
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

html.kot-dark .kitchen-login__panel {
  background: #ffffff !important;
  color: #0f172a !important;
}

html.kot-dark .kitchen-login__lead,
html.kot-dark .kitchen-login__choice,
html.kot-dark .kitchen-login__choice span {
  color: #0f172a !important;
}

html.kot-dark .kitchen-login__choice small,
html.kot-dark .kitchen-login__subtitle,
html.kot-dark .kitchen-login__footer {
  color: #475569 !important;
}

html.kot-dark .kitchen-login__back {
  background: transparent !important;
  color: #1d4ed8 !important;
}

.kitchen-login__submit:hover:not(:disabled) {
  background: #2563eb;
}

.kitchen-login__submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.kitchen-login__footer {
  margin: 24px 0 0;
  font-size: 12px;
  color: #94a3b8;
}
</style>
